// Invite links (RFC-1371 §7.1).
//
// Owner/Admin only — the caller gates on `canManageMembers` before rendering
// this, and the panel does not second-guess it.
//
// The shape of this UI is dictated by one storage decision: §7.1 persists a
// token HASH, never the token. So the link is returned exactly once, by the
// create call, and no later GET can reproduce it. That is why the freshly
// minted link gets a dedicated, hard-to-miss block with a copy button, and why
// the list below it offers "revoke" but never "copy again" — offering a copy
// control that cannot work is worse than not offering one.
//
// Invites are bearer links by design. The audience control is the exact-domain
// JWT gate at token validation, not the link itself, so the panel does not
// pretend a link is a personal invitation addressed to someone.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy, Link2 } from 'lucide-react'

import { track } from '../../analytics/events'
import type {
  CopyInviteLinkSucceededProps,
  SettingsViewEntryPoint,
} from '../../analytics/generated/analyticsEvents'
import { inviteExpiryBucket } from '../../analytics/settingsAnalytics'
import {
  INVITE_MAX_TTL_DAYS,
  TeamsRequestError,
  buildInviteUrl,
  type InvitableRole,
  type Team,
  type TeamInvite,
} from '../../api/teams'
import { useCreateInvite, useRevokeInvite, useTeamInvites } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import ListboxSelect from './ListboxSelect'
import { Card, EmptyState, ErrorText, PrimaryButton, SectionLabel } from './teamsChrome'

// Days, matching the server's `expires_in_days` (1..30). Kept as three coarse
// choices rather than a free number field: an invite TTL is a judgement about
// how long a bearer link should stay live, and three options make that choice
// legible where a spinner makes it arbitrary.
const EXPIRY_CHOICES: readonly number[] = [1, 7, INVITE_MAX_TTL_DAYS]

const INVITABLE_ROLES: readonly InvitableRole[] = ['member', 'admin']

export default function TeamInvitePanel({
  team,
  analyticsEntryPoint,
}: {
  team: Team
  /** How Settings was opened, for create/copy invite analytics (RFC-1577). */
  analyticsEntryPoint?: SettingsViewEntryPoint
}) {
  const { t, i18n } = useTranslation('settings')
  const locale = i18n.language
  const invites = useTeamInvites(team.id, true)
  const createInvite = useCreateInvite()
  const revokeInvite = useRevokeInvite()

  const [role, setRole] = useState<InvitableRole>('member')
  const [expiresInDays, setExpiresInDays] = useState<number>(7)
  const [freshLink, setFreshLink] = useState<string | null>(null)
  // Analytics shape of the invite behind `freshLink`, captured from the create
  // RESPONSE (not the pickers, which the user may change afterwards) so a later
  // copy describes the link actually copied. Ids and enums only — never the
  // token or the URL.
  const [freshInvite, setFreshInvite] = useState<CopyInviteLinkSucceededProps | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const atLimit = team.memberCount >= team.memberLimit

  const handleCreate = () => {
    setError(null)
    setCopied(false)
    // create_invite_link_clicked (analytics doc): the enabled click itself, from
    // the pickers' current values. Creation is measured from backend facts.
    const clickBucket = inviteExpiryBucket(expiresInDays)
    track('create_invite_link_clicked', {
      team_id: team.id,
      invite_role: role,
      actor_role: team.myRole,
      ...(clickBucket !== undefined ? { expiry_bucket: clickBucket } : {}),
      ...(analyticsEntryPoint !== undefined ? { entry_point: analyticsEntryPoint } : {}),
    })
    createInvite.mutate(
      { teamId: team.id, roleOnJoin: role, expiresInDays, maxUses: null },
      {
        // The API returns a raw TOKEN, not a URL — it does not know the console
        // origin. This is the only response that will ever carry it.
        onSuccess: (result) => {
          setFreshLink(buildInviteUrl(result.token))
          const bucket = inviteExpiryBucket(expiresInDays)
          setFreshInvite({
            team_id: team.id,
            invite_id: result.invite.id,
            invite_role: result.invite.roleOnJoin,
            actor_role: team.myRole,
            ...(bucket !== undefined ? { expiry_bucket: bucket } : {}),
            ...(analyticsEntryPoint !== undefined ? { entry_point: analyticsEntryPoint } : {}),
          })
        },
        onError: (err) => {
          if (!(err instanceof TeamsRequestError)) {
            setError(t('settingsModal.teams.inviteCreateFailed'))
            return
          }
          if (err.code === 'member_limit_reached') {
            setError(t('settingsModal.teams.memberLimitReached', { limit: team.memberLimit }))
            return
          }
          setError(err.message_ ?? t('settingsModal.teams.inviteCreateFailed'))
        },
      },
    )
  }

  const handleCopy = (url: string) => {
    // Clipboard access can be denied (insecure origin, permissions policy).
    // Report it rather than showing a tick for something that did not happen —
    // the user would paste the previous clipboard contents into chat.
    void navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true)
        setError(null)
        // Only after the write resolved — a denied clipboard is not a copy.
        if (freshInvite !== null) track('copy_invite_link_succeeded', freshInvite)
      })
      .catch(() => setError(t('settingsModal.teams.copyFailed')))
  }

  const handleRevoke = (invite: TeamInvite) => {
    setError(null)
    revokeInvite.mutate(
      { teamId: team.id, inviteId: invite.id },
      {
        onError: (err) => {
          const specific = err instanceof TeamsRequestError ? err.message_ : null
          setError(specific ?? t('settingsModal.teams.revokeFailed'))
        },
      },
    )
  }

  const liveInvites = (invites.data ?? []).filter((invite) => invite.revokedAt === null)

  return (
    <div data-testid="teams-invite-panel">
      <SectionLabel>{t('settingsModal.teams.invites')}</SectionLabel>

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 text-xs font-medium text-white/50">
            {t('settingsModal.teams.inviteRole')}
            <ListboxSelect
              ariaLabel={t('settingsModal.teams.inviteRole')}
              value={role}
              options={INVITABLE_ROLES}
              labelFor={(v) => t(`settingsModal.teams.role.${v}`)}
              onSelect={setRole}
              triggerTestId="teams-invite-role"
              menuTestId="teams-invite-role-menu"
              optionTestId={(v) => `teams-invite-role-option-${v}`}
            />
          </div>

          <div className="flex flex-col gap-1 text-xs font-medium text-white/50">
            {t('settingsModal.teams.inviteExpiry')}
            <ListboxSelect
              ariaLabel={t('settingsModal.teams.inviteExpiry')}
              value={String(expiresInDays)}
              options={EXPIRY_CHOICES.map(String)}
              labelFor={(v) => t('settingsModal.teams.inviteExpiryDays', { days: Number(v) })}
              onSelect={(v) => setExpiresInDays(Number(v))}
              triggerTestId="teams-invite-expiry"
              menuTestId="teams-invite-expiry-menu"
              optionTestId={(v) => `teams-invite-expiry-option-${v}`}
            />
          </div>

          <div className="ml-auto">
            <PrimaryButton
              testId="teams-invite-create"
              onClick={handleCreate}
              disabled={createInvite.isPending || atLimit}
            >
              {t('settingsModal.teams.createInvite')}
            </PrimaryButton>
          </div>
        </div>

        {atLimit && (
          <p className="mt-2 text-xs font-medium text-white/50" data-testid="teams-invite-at-limit">
            {t('settingsModal.teams.memberLimitReached', { limit: team.memberLimit })}
          </p>
        )}

        {freshLink !== null && (
          <div
            data-testid="teams-invite-fresh-link"
            className="mt-3 rounded-xl border border-white/15 bg-white/[0.08] px-3 py-2.5"
          >
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-white/60">
              <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
              {t('settingsModal.teams.inviteShownOnce')}
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                data-testid="teams-invite-link-value"
                aria-label={t('settingsModal.teams.inviteLink')}
                value={freshLink}
                onFocus={(event) => event.currentTarget.select()}
                className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/30 px-2 py-1.5 text-xs font-medium text-white"
              />
              <button
                type="button"
                data-testid="teams-invite-copy"
                onClick={() => handleCopy(freshLink)}
                className="flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-white/[0.12] px-3 text-xs font-medium text-white transition-colors hover:bg-white/20"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? t('settingsModal.teams.copied') : t('settingsModal.teams.copy')}
              </button>
            </div>
          </div>
        )}
      </Card>

      <div className="mt-3">
        {invites.isLoading ? (
          <EmptyState testId="teams-invites-loading">
            {t('settingsModal.teams.loadingInvites')}
          </EmptyState>
        ) : liveInvites.length === 0 ? (
          <EmptyState testId="teams-invites-empty">
            {t('settingsModal.teams.noInvites')}
          </EmptyState>
        ) : (
          <Card>
            {liveInvites.map((invite, index) => (
              <div
                key={invite.id}
                data-testid={`teams-invite-${invite.id}`}
                className={cn(
                  'flex items-center gap-3 px-4 py-2.5',
                  index > 0 && 'border-t border-white/[0.06]',
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white">
                    {t(`settingsModal.teams.role.${invite.roleOnJoin}`)}
                  </p>
                  <p className="text-xs font-medium text-white/45">
                    {t('settingsModal.teams.inviteMeta', {
                      expires: new Date(invite.expiresAt).toLocaleDateString(locale),
                      used: invite.useCount,
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`teams-invite-revoke-${invite.id}`}
                  disabled={revokeInvite.isPending}
                  onClick={() => handleRevoke(invite)}
                  className={cn(
                    'h-8 shrink-0 cursor-pointer rounded-full bg-white/[0.12] px-3 text-xs font-medium',
                    'text-white transition-colors hover:bg-destructive/20 hover:text-destructive',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  {t('settingsModal.teams.revoke')}
                </button>
              </div>
            ))}
          </Card>
        )}
      </div>

      {error !== null && <ErrorText testId="teams-invite-error">{error}</ErrorText>}
    </div>
  )
}
